import { type FC, Fragment, useMemo } from "react";
import type { Components } from "react-markdown";
import type { ChatbotLinkContext } from "~/pages/appointments/components/chatbotLinks";
import {
  type DataBlockMap,
  dataBlockKind,
  MARKDOWN_BLOCK_KIND,
} from "~/pages/appointments/hooks/useAgentChat";
import { ChatMarkdown } from "./ChatMarkdown";
import {
  inlineMarkerKeys,
  splitMessageSegments,
} from "./chatMessageBlocks/messageSegments";
import { resolveBlockRenderer } from "./chatMessageBlocks/registry";

interface ChatMessageBodyProps {
  /** Assistant reply text, markdown blocks already spliced in. */
  content: string;
  /** Typed blocks the remaining `[data:tN]` markers resolve against. */
  dataBlocks?: DataBlockMap;
  markdownComponents: Components;
  /**
   * The click context the markdown links already dispatch through, so a
   * card's row click and a link click behave identically — including a
   * caller's viewer overrides.
   */
  linkContext: ChatbotLinkContext;
  /** True while this reply is still streaming — see `splitMessageSegments`. */
  streaming?: boolean;
  /** A stopped reply can still contain unfinished formatting. */
  stopped?: boolean;
}

/**
 * Renders an assistant reply: prose interleaved with entity components.
 *
 * The model positions each typed data block with a `[data:tN]` marker, so
 * the text is split on markers and each piece rendered by the thing that
 * owns it — text through the existing react-markdown setup, blocks through
 * the `kind` registry.
 *
 * Two degradations, both deliberate: a kind this bundle doesn't know (or a
 * payload it can't parse) renders the block's markdown fallback, and a
 * marker whose block hasn't arrived yet renders nothing rather than raw
 * marker text. Blocks stream before generation, so in practice the block
 * usually wins the race.
 */
export const ChatMessageBody: FC<ChatMessageBodyProps> = ({
  content,
  dataBlocks,
  markdownComponents,
  linkContext,
  streaming = false,
  stopped = false,
}) => {
  // A marker the model put somewhere other than its own line is spliced back
  // into the prose as markdown, so the list / quote / table around it stays
  // intact. Only block-position markers reach the component registry.
  const inlineKeys = inlineMarkerKeys(content);
  const text = useMemo(() => {
    if (!dataBlocks || inlineKeys.size === 0) return content;
    let out = content;
    for (const key of inlineKeys) {
      const block = dataBlocks.get(key);
      if (block) out = out.split(block.marker).join(block.content);
    }
    return out;
    // inlineKeys is derived from content; listing it would rebuild the set
    // identity every render for no gain.
    // biome-ignore lint/correctness/useExhaustiveDependencies: derived from content
  }, [content, dataBlocks]);
  const segments = splitMessageSegments(text, { streaming });

  return (
    <>
      {segments.map((segment, i) => {
        if (segment.type === "text") {
          return (
            <ChatMarkdown
              key={i}
              text={segment.text}
              components={markdownComponents}
              streaming={streaming}
              stopped={stopped}
            />
          );
        }

        const block = dataBlocks?.get(segment.key);
        if (!block) return null;

        const fallback = (
          <ChatMarkdown
            text={block.content}
            components={markdownComponents}
            streaming={streaming}
            stopped={stopped}
          />
        );
        const kind = dataBlockKind(block);
        const Renderer =
          kind === MARKDOWN_BLOCK_KIND ? null : resolveBlockRenderer(kind);
        if (!Renderer) return <Fragment key={i}>{fallback}</Fragment>;

        return (
          <Renderer
            key={i}
            block={block}
            ctx={linkContext}
            fallback={fallback}
          />
        );
      })}
    </>
  );
};
